/**
 * Voice-to-Work-Order Page.
 *
 * Speech-to-structured-data interface for hands-free work order creation.
 * Uses the browser's SpeechRecognition API for voice capture and the
 * backend's voice parsing service for field extraction.
 */

import { useState, useRef } from "react";
import { Mic, MicOff, Send, Wand2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";

/* eslint-disable @typescript-eslint/no-explicit-any */
// Web Speech API types — not in all TS DOM libs.
type SpeechRecognitionType = any;
type SpeechRecognitionEventType = any;

interface WindowWithSpeech {
  SpeechRecognition?: new () => SpeechRecognitionType;
  webkitSpeechRecognition?: new () => SpeechRecognitionType;
}

interface ParsedWO {
  asset_id: string | null;
  asset_info: {
    id: string;
    sku: string;
    name: string;
    category: string;
  } | null;
  title: string;
  description: string;
  work_type: string;
  priority: string;
  estimated_hours: number | null;
  method: string;
  ai_enabled: boolean;
  original_transcript: string;
}

function VoiceWOPage() {
  const navigate = useNavigate();
  const [transcript, setTranscript] = useState("");
  const [assetSku, setAssetSku] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [parsed, setParsed] = useState<ParsedWO | null>(null);
  const recognitionRef = useRef<SpeechRecognitionType | null>(null);

  // Editable fields after parsing.
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editType, setEditType] = useState("repair");
  const [editPriority, setEditPriority] = useState("medium");
  const [editHours, setEditHours] = useState("");

  const parseMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/voice-wo/parse", {
          transcript,
          asset_sku: assetSku || undefined,
        })
      ).data as ParsedWO,
    onSuccess: (data) => {
      setParsed(data);
      setEditTitle(data.title);
      setEditDesc(data.description);
      setEditType(data.work_type);
      setEditPriority(data.priority);
      setEditHours(data.estimated_hours?.toString() || "");
    },
  });

  const createWOMutation = useMutation({
    mutationFn: async () => {
      if (!parsed?.asset_id) throw new Error("No asset selected");
      return (
        await apiClient.post("/api/v1/work-orders", {
          asset_id: parsed.asset_id,
          title: editTitle,
          description: editDesc,
          work_type: editType,
          priority: editPriority,
          estimated_hours: editHours ? parseFloat(editHours) : undefined,
          notes: `[Voice transcribed] ${parsed.original_transcript}`,
        })
      ).data;
    },
    onSuccess: (data) => navigate(`/work-orders/${data.id}`),
  });

  const startListening = () => {
    const w = window as unknown as WindowWithSpeech;
    const SpeechRecognitionCtor =
      w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      toast.error("Speech recognition is not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event: SpeechRecognitionEventType) => {
      let final = "";
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript + " ";
        }
      }
      if (final) setTranscript((prev) => prev + final);
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Voice-to-Work-Order
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Dictate work orders hands-free. Speak or type a description and we'll
          extract the details.
        </p>
      </div>

      {/* Voice Input */}
      <Card>
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={isListening ? stopListening : startListening}
            className={`flex h-12 w-12 items-center justify-center rounded-full transition-all ${
              isListening
                ? "bg-red-500 text-white animate-pulse"
                : "bg-brand-200 text-brand-700 hover:bg-brand-200 dark:bg-brand-900/50 dark:text-brand-300"
            }`}
          >
            {isListening ? (
              <MicOff className="h-6 w-6" />
            ) : (
              <Mic className="h-6 w-6" />
            )}
          </button>
          <div>
            <p className="text-sm font-medium text-[var(--text-primary)]">
              {isListening
                ? "Listening... speak now"
                : "Tap to start recording"}
            </p>
            <p className="text-xs text-[var(--text-tertiary)]">
              Or type your description below
            </p>
          </div>
        </div>

        <textarea
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          rows={4}
          placeholder="Example: The hydraulic cylinder on the backhoe is leaking. Needs urgent repair. Probably about 3 hours of work."
          className="w-full rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] p-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        />

        <div className="mt-3 flex gap-3 items-end">
          <Input
            label="Asset SKU (optional)"
            value={assetSku}
            onChange={(e) => setAssetSku(e.target.value)}
            placeholder="TR-EQ-000042"
          />
          <Button
            onClick={() => parseMutation.mutate()}
            isLoading={parseMutation.isPending}
            disabled={transcript.length < 5}
          >
            <Wand2 className="h-4 w-4" /> Parse
          </Button>
        </div>
      </Card>

      {/* Parsed Result — Editable Draft */}
      {parsed && (
        <Card className="border-2 border-brand-500/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                Parsed Work Order Draft
              </h3>
              <p className="text-xs text-[var(--text-tertiary)]">
                Method:{" "}
                <Badge variant={parsed.ai_enabled ? "info" : "default"}>
                  {parsed.method}
                </Badge>
              </p>
            </div>
            {parsed.asset_info && (
              <div className="text-right">
                <p className="text-xs font-semibold text-brand-600">
                  {parsed.asset_info.sku}
                </p>
                <p className="text-xs text-[var(--text-tertiary)]">
                  {parsed.asset_info.name}
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)]">
                  Work Type
                </label>
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                  className="mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm"
                >
                  {[
                    "repair",
                    "maintenance",
                    "refurbishment",
                    "inspection",
                    "conditioning",
                  ].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)]">
                  Priority
                </label>
                <select
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value)}
                  className="mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm"
                >
                  {["low", "medium", "high", "urgent"].map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Description
              </label>
              <textarea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                rows={3}
                className="mt-1.5 w-full rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] p-3 text-sm text-[var(--text-primary)]"
              />
            </div>
            <Input
              label="Estimated Hours"
              type="number"
              step="0.5"
              value={editHours}
              onChange={(e) => setEditHours(e.target.value)}
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setParsed(null)}
            >
              Discard
            </Button>
            <Button
              size="sm"
              onClick={() => createWOMutation.mutate()}
              isLoading={createWOMutation.isPending}
              disabled={!parsed.asset_id || !editTitle}
            >
              <Send className="h-4 w-4" /> Create Work Order
            </Button>
          </div>

          {!parsed.asset_id && (
            <p className="mt-2 text-xs text-[var(--text-warning)]">
              No asset matched. Enter an Asset SKU above and re-parse, or create
              the WO manually.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

export default VoiceWOPage;
